import { Controller } from '@hotwired/stimulus'

export default class DashboardChartsRebrandingController extends Controller {
  static targets = [
    'typologiesChart',
    'statusesChart',
    'repaymentsChart',
    'outstandingCapitalChart',
    'typologiesChartLoader',
    'statusesChartLoader',
    'repaymentsChartLoader',
    'outstandingCapitalChartLoader',
    'profileId',
  ];

  static values = {
    url: String,
  };

  connect() {
    this.requestChartsData();
  }

  requestChartsData() {
    const controller = this;

    App.cable.subscriptions.create(
      {
        channel: 'DashboardChartsChannel',
        profile_id: controller.profileIdTarget.dataset['profileId'],
      },
      {
        received(data) {
          if (data.status == 'success') {
            JSON.parse(data.charts_data).forEach((chartData) => {
              controller.renderChart(chartData);
            });
          } else {
            controller.typologiesChartLoaderTarget.remove();
            controller.statusesChartLoaderTarget.remove();
            controller.repaymentsChartLoaderTarget.remove();
            controller.outstandingCapitalChartLoaderTarget.remove();
            toastr.error(
              'Une erreur est survenue lors du chargement de vos données veuillez recharger la page.'
            );
          }

          this.unsubscribe();
        },
        disconnected(data) {
          this.unsubscribe();
        },
      }
    );
  }

  renderChart(chartData) {
    const chartTarget = this.chartTargetFor(chartData.name);
    const chartLoaderTarget = this.chartLoaderTargetFor(chartData.name);
    const existingChart = Chart.getChart(chartTarget);

    if (!!existingChart) {
      existingChart.destroy();
    }

    if (chartData.name === 'outstanding-capital-chart') {
      const gradient = chartTarget
        .getContext('2d')
        .createLinearGradient(0, 0, 0, 400);
      const max = Math.max(...chartData.datasets[0].data);
      const min = Math.min(...chartData.datasets[0].data);

      switch (true) {
        case max <= 0 && min <= 0:
          gradient.addColorStop(0, 'rgba(255, 255, 255, 1)');
          gradient.addColorStop(1, 'rgba(67, 101, 163, 0.01)');
          break;
        case max > 0 && min <= 0: {
          const absoluteMax = Math.abs(max) + Math.abs(min);
          const percentMax = max / absoluteMax;

          gradient.addColorStop(0, 'rgba(67, 101, 163, 0.01)');
          gradient.addColorStop(1, 'rgba(255, 255, 255, 1)');
          break;
        }
        case max > 0 && min > 0:
          gradient.addColorStop(0, 'rgba(67, 101, 163, 0.01)');
          gradient.addColorStop(1, 'rgba(255, 255, 255, 1)');
          break;
      }

      chartData.datasets[0].backgroundColor = gradient;
    }

    chartLoaderTarget.remove();

    new Chart(chartTarget, {
      type: chartData.type,
      data: chartData,
      options: this.chartOptionsFor(chartData.name),
      plugins: this.chartPluginsFor(chartData.name),
    });
  }

  chartOptionsFor(chartName) {
    switch (chartName) {
      case 'outstanding-capital-chart':
        return this.lineChartOptions();
      case 'project-typologies-chart':
      case 'project-statuses-chart':
        return this.pieChartOptions(chartName);
      case 'repayments-chart':
        return this.barChartOptions();
      default:
        return {};
    }
  }

  chartPluginsFor(chartName) {
    switch (chartName) {
      case 'project-typologies-chart':
      case 'project-statuses-chart':
        return [PieCustomLegendPluginRebranding.htmlLegendPlugin()];
      default:
        return [];
    }
  }

  chartTargetFor(chartName) {
    switch (chartName) {
      case 'project-typologies-chart':
        return this.typologiesChartTarget;
      case 'project-statuses-chart':
        return this.statusesChartTarget;
      case 'repayments-chart':
        return this.repaymentsChartTarget;
      case 'outstanding-capital-chart':
        return this.outstandingCapitalChartTarget;
    }
  }

  chartLoaderTargetFor(chartName) {
    switch (chartName) {
      case 'project-typologies-chart':
        return this.typologiesChartLoaderTarget;
      case 'project-statuses-chart':
        return this.statusesChartLoaderTarget;
      case 'repayments-chart':
        return this.repaymentsChartLoaderTarget;
      case 'outstanding-capital-chart':
        return this.outstandingCapitalChartLoaderTarget;
    }
  }

  pieChartOptions(chartName) {
    return {
      plugins: {
        htmlLegend: {
          containerID: `${chartName}-legend`,
        },
        legend: {
          display: false,
        },
        tooltip: {
          callbacks: {
            label: function (context) {
              let suffix = 'projets';
              if (context.formattedValue === 1) {
                suffix = 'projet';
              }

              return `${context.formattedValue} ${suffix}`;
            },
          },
        },
      },
      aspectRatio: 1.5,
      responsive: true,
      cutout: '75%',
    };
  }

  barChartOptions() {
    const controller = this;

    return {
      plugins: {
        legend: {
          position: 'bottom',
          labels: {
            boxWidth: 4,
            boxHeight: 4,
            padding: 16,
            usePointStyle: true,
          },
        },
        tooltip: {
          callbacks: {
            label: function (context) {
              return controller.labelWithCurrency(context);
            },
          },
        },
      },
      interaction: {
        intersect: true,
      },
      aspectRatio: 1.5,
      scales: {
        x: {
          stacked: true,
          grid: {
            display: false,
          },
          ticks: {
            callback: function (val) {
              return this.getLabelForValue(val).slice(0, -1);
            },
            maxRotation: 0,
            minRotation: 0,
          },
        },
        y: {
          beginAtZero: true,
          stacked: true,
          grid: {
            lineWidth: 0.5,
          },
          max: function (context) {
            const defaultMaxAmount = 2000;

            const defaultAmountNeeded = context.chart.data.datasets.every(
              function (dataset) {
                return dataset.data.every(function (val) {
                  return parseFloat(val) === 0;
                });
              }
            );

            if (defaultAmountNeeded) {
              return defaultMaxAmount;
            }
          },
        },
      },
      responsive: true,
    };
  }

  lineChartOptions() {
    const controller = this;

    return {
      plugins: {
        legend: {
          display: false,
        },
        tooltip: {
          mode: 'index',
          intersect: false,
          callbacks: {
            label: function (context) {
              return controller.labelWithCurrency(context);
            },
          },
        },
      },
      hover: {
        mode: 'nearest',
        intersect: false,
      },
      interaction: {
        intersect: true,
      },
      aspectRatio: 1.5,
      scales: {
        x: {
          grid: {
            display: false,
          },
          ticks: {
            callback: function (val, index) {
              if (index % 3 === 0) {
                return this.getLabelForValue(val);
              }
            },
            maxRotation: 0,
            minRotation: 0,
          },
        },
        y: {
          beginAtZero: true,
          grid: {
            display: false,
          },
          border: {
            display: window.innerWidth > 768,
          },
          ticks: {
            callback: function (value) {
              if (value % 10 === 0) {
                return value;
              }
            },
            display: window.innerWidth > 768,
          },
          // Value is added to the maximum data value and subtracted from the minimum data
          grace: '15%',
          // DefaultMaxAmount is 2000 but we need to count the grace value
          // that add 15% to 1740
          suggestedMax: 1740,
        },
      },
      responsive: true,
    };
  }

  labelWithCurrency(context) {
    const label = context.dataset.label || '';

    if (label) {
      return `${label}: ${context.formattedValue} €`;
    } else {
      return `${context.formattedValue} €`;
    }
  }
}
